import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  createOffer,
  getCandidates,
  getOffers,
  sendOfferLetter,
  updateOffer,
} from "../api/recruitmentApi";
import { formatDate, normalizeList, statusClass } from "./hrUtils";
import "./HRShared.css";
import "./Offers.css";

const OFFER_STATUSES = [
  "Offer Pending",
  "Offer Sent",
  "Offer Accepted",
  "Offer Declined",
  "Joining Confirmed",
  "Joined",
  "Did Not Join",
];

const getToday = () => new Date().toISOString().slice(0, 10);

const getEmptyForm = () => ({
  candidate: "",
  offeredDesignation: "",
  offeredSalary: "",
  offerDate: getToday(),
  expectedJoiningDate: "",
  status: "Offer Pending",
  remarks: "",
  recipients: [],
  subject: "Offer Letter from SDC Studio",
  message:
    "Dear Candidate,\n\n" +
    "Congratulations!\n\n" +
    "We are pleased to offer you an opportunity to join SDC Studio.\n\n" +
    "Please find the offer letter attached with this email. Kindly review the terms and confirm your acceptance.\n\n" +
    "Regards,\nHR Team\nSDC Studio",
  attachments: [],
});

const unwrapData = (response) =>
  response?.data?.data ?? response?.data?.offer ?? response?.data ?? response;

const normalizeCandidateList = (response) => {
  const data = response?.data ?? response;
  const possibleLists = [
    data,
    data?.data,
    data?.candidates,
    data?.items,
    data?.results,
    data?.docs,
  ];

  const list = possibleLists.find(Array.isArray);
  return Array.isArray(list) ? list : [];
};

const normalizeOfferList = (response) => {
  const normalized = normalizeList(response);
  if (Array.isArray(normalized)) return normalized;

  const data = response?.data ?? response;
  return data?.offers || data?.items || data?.results || data?.docs || [];
};

const getCandidateEmail = (candidate) =>
  String(
    candidate?.email ||
      candidate?.candidateEmail ||
      candidate?.personalEmail ||
      "",
  )
    .trim()
    .toLowerCase();

const getCandidateName = (candidate) =>
  candidate?.candidateName ||
  candidate?.name ||
  candidate?.fullName ||
  candidate?.contactName ||
  "Candidate";

const getCandidateDesignation = (candidate) =>
  candidate?.jobOpening?.jobTitle ||
  candidate?.jobOpening?.title ||
  candidate?.jobTitle ||
  candidate?.appliedPosition ||
  candidate?.position ||
  candidate?.designation ||
  "";

const dedupeEmails = (emails = []) => [
  ...new Set(
    emails
      .map((email) =>
        String(email || "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean),
  ),
];

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

const Offers = () => {
  const [items, setItems] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [form, setForm] = useState(getEmptyForm());
  const [editingId, setEditingId] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const selectedCandidate = useMemo(
    () =>
      candidates.find(
        (candidate) => String(candidate._id) === String(form.candidate),
      ) || null,
    [candidates, form.candidate],
  );

  const loadSelectedCandidates = async () => {
    try {
      setCandidateLoading(true);

      /*
       * Pehle backend ko status=Selected filter ke saath call karte hain.
       * Agar backend query filter ignore kare ya empty response de, tab all
       * candidates fetch honge, lekin frontend sirf exact Selected status
       * wale candidates ko hi dropdown me rakhega.
       */
      let response = await getCandidates({
        stage: "Selected",
        limit: 500,
      });

      let list = normalizeCandidateList(response);

      if (!list.length) {
        response = await getCandidates({
          limit: 500,
        });

        list = normalizeCandidateList(response);
      }

      const selectedCandidates = list.filter(
        (candidate) =>
          String(candidate?.stage || "")
            .trim()
            .toLowerCase() === "selected",
      );

      const uniqueSelectedCandidates = Array.from(
        new Map(
          selectedCandidates
            .filter((candidate) => candidate?._id)
            .map((candidate) => [String(candidate._id), candidate]),
        ).values(),
      );

      setCandidates(uniqueSelectedCandidates);

      console.log(
        "Selected candidates available for Offer:",
        uniqueSelectedCandidates,
      );
    } catch (requestError) {
      console.error("Unable to load selected candidates:", requestError);

      setCandidates([]);
      throw requestError;
    } finally {
      setCandidateLoading(false);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");

      const [offerResponse] = await Promise.all([
        getOffers(),
        loadSelectedCandidates(),
      ]);

      setItems(normalizeOfferList(offerResponse));
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to load offers or selected candidates.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!modalOpen) return undefined;

    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = oldOverflow;
    };
  }, [modalOpen]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const closeModal = () => {
    if (saving || sending) return;

    setModalOpen(false);
    setEditingId("");
    setForm(getEmptyForm());
  };

  const openCreate = async () => {
    setEditingId("");
    setForm(getEmptyForm());
    setError("");
    setSuccess("");
    setModalOpen(true);

    if (!candidates.length) {
      try {
        await loadSelectedCandidates();
      } catch {
        setError("Unable to load selected candidates.");
      }
    }
  };

  const openEdit = (item) => {
    const candidateEmail = getCandidateEmail(item.candidate);
    const existingRecipients = Array.isArray(item.recipients)
      ? item.recipients
      : item.sentTo
        ? [item.sentTo]
        : candidateEmail
          ? [candidateEmail]
          : [];

    setEditingId(item._id);
    setForm({
      ...getEmptyForm(),
      ...item,
      candidate: item.candidate?._id || item.candidate || "",
      offeredSalary: item.offeredSalary ?? "",
      offerDate: String(item.offerDate || "").slice(0, 10) || getToday(),
      expectedJoiningDate: String(item.expectedJoiningDate || "").slice(0, 10),
      recipients: dedupeEmails(existingRecipients),
      subject:
        item.emailSubject ||
        item.subject ||
        `Offer Letter from SDC Studio${
          item.offeredDesignation ? ` - ${item.offeredDesignation}` : ""
        }`,
      message: item.emailMessage || item.message || getEmptyForm().message,
      attachments: [],
    });

    setError("");
    setSuccess("");
    setModalOpen(true);
  };

  const handleCandidateChange = (candidateId) => {
    const candidate = candidates.find(
      (entry) => String(entry._id) === String(candidateId),
    );

    const email = getCandidateEmail(candidate);
    const candidateName = getCandidateName(candidate);
    const designation = getCandidateDesignation(candidate);

    setForm((current) => ({
      ...current,
      candidate: candidateId,
      offeredDesignation: designation || current.offeredDesignation,
      recipients: email ? [email] : [],
      subject: designation
        ? `Offer Letter from SDC Studio - ${designation}`
        : "Offer Letter from SDC Studio",
      message:
        `Dear ${candidateName},\n\n` +
        "Congratulations!\n\n" +
        `We are pleased to offer you the position of ${
          designation || "the selected role"
        } at SDC Studio.\n\n` +
        "Please find the offer letter attached with this email. Kindly review the terms and confirm your acceptance.\n\n" +
        "Regards,\nHR Team\nSDC Studio",
    }));
  };

  const handleAttachmentChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024;
    const oversized = selectedFiles.find((file) => file.size > maxSize);

    if (oversized) {
      setError(`${oversized.name} is larger than 10 MB.`);
      event.target.value = "";
      return;
    }

    setForm((current) => ({
      ...current,
      attachments: [...current.attachments, ...selectedFiles].slice(
        0,
        maxFiles,
      ),
    }));

    event.target.value = "";
  };

  const removeAttachment = (index) => {
    setForm((current) => ({
      ...current,
      attachments: current.attachments.filter(
        (_file, fileIndex) => fileIndex !== index,
      ),
    }));
  };

  const validateForm = (shouldSend) => {
    if (!form.candidate) return "Please select a candidate.";
    if (!String(form.offeredDesignation || "").trim()) {
      return "Offered designation is required.";
    }
    if (!Number(form.offeredSalary)) return "Offered salary is required.";

    if (shouldSend) {
      if (!form.recipients.length) {
        return "At least one recipient email is required.";
      }
      if (form.recipients.some((email) => !isValidEmail(email))) {
        return "One or more recipient email addresses are invalid.";
      }
      if (!String(form.subject || "").trim()) {
        return "Email subject is required.";
      }
      if (!String(form.message || "").trim()) {
        return "Email message is required.";
      }
      if (!form.attachments.length) {
        return "Please attach the final offer letter before sending.";
      }
    }

    return "";
  };

  const buildOfferPayload = () => ({
    candidate: form.candidate,
    offeredDesignation: String(form.offeredDesignation || "").trim(),
    offeredSalary: Number(form.offeredSalary),
    offerDate: form.offerDate,
    expectedJoiningDate: form.expectedJoiningDate || null,
    status: form.status,
    remarks: String(form.remarks || "").trim(),
  });

  const saveOfferRecord = async () => {
    const payload = buildOfferPayload();
    const response = editingId
      ? await updateOffer(editingId, payload)
      : await createOffer(payload);

    const savedOffer = unwrapData(response);
    const offerId = savedOffer?._id || editingId;

    if (!offerId) {
      throw new Error("Offer was saved but offer ID was not returned.");
    }

    return offerId;
  };

  const saveOffer = async (event, shouldSend = false) => {
    event.preventDefault();

    const validationError = validateForm(shouldSend);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setSending(shouldSend);
      setError("");
      setSuccess("");

      const offerId = await saveOfferRecord();

      if (shouldSend) {
        const emailPayload = new FormData();
        emailPayload.append("recipients", JSON.stringify(form.recipients));
        emailPayload.append("subject", form.subject.trim());
        emailPayload.append("message", form.message.trim());

        form.attachments.forEach((file) => {
          emailPayload.append("attachments", file);
        });

        const sendResponse = await sendOfferLetter(offerId, emailPayload);
        setSuccess(
          sendResponse?.data?.message || "Offer letter sent successfully.",
        );
      } else {
        setSuccess(
          editingId ? "Offer updated successfully." : "Offer saved as draft.",
        );
      }

      setModalOpen(false);
      setEditingId("");
      setForm(getEmptyForm());
      await loadData();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to save or send the offer.",
      );
    } finally {
      setSaving(false);
      setSending(false);
    }
  };

  return (
    <div className="hr-page hr-offers-page">
      <header className="hr-page-header">
        <div>
          <h1>Offers</h1>
          <p>Manage salary offers, acceptance and expected joining dates.</p>
        </div>

        <button
          type="button"
          className="hr-btn hr-btn-primary"
          onClick={openCreate}
        >
          + Create Offer
        </button>
      </header>

      {error && <div className="hr-error">{error}</div>}
      {success && <div className="hr-offer-success">{success}</div>}

      {loading ? (
        <div className="hr-card hr-loading">Loading offers...</div>
      ) : (
        <div className="hr-table-wrap hr-offer-table-wrap">
          <table className="hr-table hr-offer-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Designation</th>
                <th>Salary</th>
                <th>Offer Date</th>
                <th>Expected Joining</th>
                <th>Status</th>
                <th>Sent To</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {items.length ? (
                items.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <strong>{getCandidateName(item.candidate)}</strong>
                      <small>{getCandidateEmail(item.candidate)}</small>
                    </td>
                    <td>
                      {item.offeredDesignation ||
                        item.jobOpening?.jobTitle ||
                        "-"}
                    </td>
                    <td>
                      {item.offeredSalary
                        ? `₹${Number(item.offeredSalary).toLocaleString("en-IN")}`
                        : "-"}
                    </td>
                    <td>{formatDate(item.offerDate)}</td>
                    <td>{formatDate(item.expectedJoiningDate)}</td>
                    <td>
                      <span className={`hr-status ${statusClass(item.status)}`}>
                        {item.status || "Offer Pending"}
                      </span>
                    </td>
                    <td>
                      <span className="hr-offer-sent-to">
                        {item.sentTo || "-"}
                      </span>
                      {item.sentAt && <small>{formatDate(item.sentAt)}</small>}
                    </td>
                    <td>
                      <div className="hr-inline-actions">
                        <button
                          type="button"
                          className="hr-btn hr-btn-secondary"
                          onClick={() => openEdit(item)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="hr-btn hr-btn-primary"
                          onClick={() => openEdit(item)}
                        >
                          {item.status === "Offer Sent"
                            ? "Resend"
                            : "Send Offer"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="hr-empty">
                    No offers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div className="hr-offer-modal-backdrop" onMouseDown={closeModal}>
            <form
              className="hr-offer-modal"
              onMouseDown={(event) => event.stopPropagation()}
              onSubmit={(event) => saveOffer(event, false)}
            >
              <div className="hr-offer-modal-head">
                <div className="hr-offer-modal-icon">✉</div>
                <div className="hr-offer-modal-title">
                  <h2>
                    {editingId
                      ? "Edit & Send Offer Letter"
                      : "Create Offer Letter"}
                  </h2>
                  <p>
                    Select a candidate, attach the final offer letter and send
                    it to the candidate's registered email address.
                  </p>
                </div>
                <button
                  type="button"
                  className="hr-offer-close"
                  onClick={closeModal}
                  disabled={saving || sending}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>

              <div className="hr-offer-modal-body">
                <section className="hr-offer-section">
                  <h3>Offer Details</h3>

                  <div className="hr-offer-form-grid">
                    <div className="hr-field hr-field-full">
                      <label>Selected Candidate *</label>
                      <select
                        required
                        className="hr-select"
                        value={form.candidate}
                        onChange={(event) =>
                          handleCandidateChange(event.target.value)
                        }
                        disabled={Boolean(editingId) || candidateLoading}
                      >
                        <option value="">
                          {candidateLoading
                            ? "Loading selected candidates..."
                            : "Select candidate"}
                        </option>

                        {candidates.map((candidate) => (
                          <option value={candidate._id} key={candidate._id}>
                            {getCandidateName(candidate)}
                            {getCandidateDesignation(candidate)
                              ? ` — ${getCandidateDesignation(candidate)}`
                              : ""}
                            {getCandidateEmail(candidate)
                              ? ` — ${getCandidateEmail(candidate)}`
                              : ""}
                          </option>
                        ))}
                      </select>

                      {!candidateLoading && !candidates.length && (
                        <small>
                          No selected candidates were found. Open Interviews,
                          edit the candidate interview and set Status to
                          “Selected”.
                        </small>
                      )}

                      {!candidateLoading && candidates.length > 0 && (
                        <small>
                          {candidates.length} selected candidate
                          {candidates.length === 1 ? "" : "s"} available.
                        </small>
                      )}
                    </div>

                    <div className="hr-field">
                      <label>Offered Designation *</label>
                      <input
                        required
                        className="hr-input"
                        value={form.offeredDesignation}
                        onChange={(event) =>
                          updateField("offeredDesignation", event.target.value)
                        }
                      />
                    </div>

                    <div className="hr-field">
                      <label>Offered Salary *</label>
                      <input
                        required
                        min="1"
                        type="number"
                        className="hr-input"
                        value={form.offeredSalary}
                        onChange={(event) =>
                          updateField("offeredSalary", event.target.value)
                        }
                      />
                    </div>

                    <div className="hr-field">
                      <label>Offer Date</label>
                      <input
                        type="date"
                        className="hr-input"
                        value={form.offerDate}
                        onChange={(event) =>
                          updateField("offerDate", event.target.value)
                        }
                      />
                    </div>

                    <div className="hr-field">
                      <label>Expected Joining</label>
                      <input
                        type="date"
                        className="hr-input"
                        value={form.expectedJoiningDate}
                        onChange={(event) =>
                          updateField("expectedJoiningDate", event.target.value)
                        }
                      />
                    </div>

                    <div className="hr-field">
                      <label>Status</label>
                      <select
                        className="hr-select"
                        value={form.status}
                        onChange={(event) =>
                          updateField("status", event.target.value)
                        }
                      >
                        {OFFER_STATUSES.map((status) => (
                          <option value={status} key={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="hr-field hr-field-full">
                      <label>Offer Remarks</label>
                      <textarea
                        className="hr-textarea"
                        value={form.remarks}
                        onChange={(event) =>
                          updateField("remarks", event.target.value)
                        }
                      />
                    </div>
                  </div>
                </section>

                <section className="hr-offer-section">
                  <h3>Send Offer Letter</h3>

                  <div className="hr-offer-from-row">
                    <span>From</span>
                    <strong>hr.sdcllp@gmail.com</strong>
                  </div>

                  {selectedCandidate && (
                    <div className="hr-offer-info">
                      Selected:{" "}
                      <strong>{getCandidateName(selectedCandidate)}</strong>
                    </div>
                  )}

                  <div className="hr-field">
                    <label>Candidate Email</label>
                    <input
                      type="email"
                      className="hr-input"
                      value={form.recipients[0] || ""}
                      readOnly
                      placeholder="Candidate email will load automatically"
                    />
                    <small>
                      This email is fetched automatically from the selected
                      candidate and cannot be changed here.
                    </small>
                  </div>

                  <div className="hr-field">
                    <label>Subject *</label>
                    <input
                      className="hr-input"
                      value={form.subject}
                      onChange={(event) =>
                        updateField("subject", event.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Message *</label>
                    <textarea
                      className="hr-textarea hr-offer-message"
                      value={form.message}
                      onChange={(event) =>
                        updateField("message", event.target.value)
                      }
                    />
                  </div>

                  <div className="hr-field">
                    <label>Attach Final Offer Letter *</label>
                    <label className="hr-offer-attachment-button">
                      + Attach Offer Letter
                      <input
                        type="file"
                        multiple
                        hidden
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={handleAttachmentChange}
                      />
                    </label>

                    {!!form.attachments.length && (
                      <div className="hr-offer-attachments">
                        {form.attachments.map((file, index) => (
                          <div
                            className="hr-offer-attachment-item"
                            key={`${file.name}-${index}`}
                          >
                            <span>{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="hr-offer-info">
                    After successful delivery, the candidate stage will move to
                    <strong> Offer Sent</strong>.
                  </div>
                </section>
              </div>

              <div className="hr-offer-modal-foot">
                <button
                  type="button"
                  className="hr-btn hr-btn-secondary"
                  onClick={closeModal}
                  disabled={saving || sending}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="hr-btn hr-btn-secondary"
                  disabled={saving || sending}
                >
                  {saving && !sending ? "Saving..." : "Save Draft"}
                </button>

                <button
                  type="button"
                  className="hr-btn hr-btn-primary"
                  disabled={saving || sending}
                  onClick={(event) => saveOffer(event, true)}
                >
                  {sending ? "Sending Offer..." : "Send Offer Letter"}
                </button>
              </div>
            </form>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Offers;
